import React, { ReactElement } from 'react';
import { deviceSize } from 'src/domains/layouts/state/breakpointsState/BreakpointsState';
import {
    RenderOptimizedVariantsPropsType,
    RenderSourcePropsType,
    SourceSetPropsType,
} from 'src/domains/layouts/webview/components/common/lazyLoadingImage/common/ImagePictureHelpers.type';
import { useCommon } from 'src/domains/common/Common';
import { AppLayoutsState } from 'src/domains/layouts/state/AppLayouts.state';

const RenderSource = (props: RenderSourcePropsType): ReactElement => {
    const { imgSrc, mediaMaxWidth, format } = props;

    return (
        <source
            srcSet={imgSrc}
            type={format === undefined ? undefined : `image/${format}`}
            media={mediaMaxWidth === undefined ? undefined : `(max-width: ${mediaMaxWidth}px)`}
        />
    );
};

const isImageRelative = (src: string): boolean => {
    return src[0] === '/';
};

export const RenderOptimizedVariants = (props: RenderOptimizedVariantsPropsType): ReactElement | null => {
    const common = useCommon();
    const appLayoutsState = AppLayoutsState.get(common);

    const { img, mediaMaxWidth } = props;
    const { src, optimizeOptions } = img;

    if (isImageRelative(src)) {
        return null;
    }

    return (
        <>
            {props.formats.map((format, idx) => {
                const optimizedSrc = appLayoutsState.optimizeImage(src, format, optimizeOptions);

                if (optimizedSrc === null) {
                    return null;
                }

                return (
                    <RenderSource
                        imgSrc={optimizedSrc}
                        format={format}
                        mediaMaxWidth={mediaMaxWidth}
                        key={idx}
                    />
                );
            })}
        </>
    );
};

export const DeviceSourceSet = (props: SourceSetPropsType): ReactElement => {
    const { imgDeviceSet } = props;

    return (
        <>
            {imgDeviceSet?.mobile === undefined ? null : (
                <>
                    <RenderOptimizedVariants
                        img={{
                            src: imgDeviceSet.mobile.src,
                            optimizeOptions: { width: deviceSize.mobile, quality: imgDeviceSet.mobile.quality },
                        }}
                        mediaMaxWidth={deviceSize.mobile}
                        formats={['avif', 'webp']}
                    />
                    <RenderSource
                        imgSrc={imgDeviceSet.mobile.src}
                        mediaMaxWidth={deviceSize.mobile}
                    />
                </>
            )}
            {imgDeviceSet?.tablet === undefined ? null : (
                <>
                    <RenderOptimizedVariants
                        img={{
                            src: imgDeviceSet.tablet.src,
                            optimizeOptions: { width: deviceSize.tablet, quality: imgDeviceSet.tablet.quality },
                        }}
                        mediaMaxWidth={deviceSize.tablet}
                        formats={['avif', 'webp']}
                    />
                    <RenderSource
                        imgSrc={imgDeviceSet.tablet.src}
                        mediaMaxWidth={deviceSize.tablet}
                    />
                </>
            )}
            {imgDeviceSet?.desktop === undefined ? null : (
                <>
                    <RenderOptimizedVariants
                        img={{
                            src: imgDeviceSet.desktop.src,
                            optimizeOptions: { width: deviceSize.desktop, quality: imgDeviceSet.desktop.quality },
                        }}
                        mediaMaxWidth={deviceSize.desktop}
                        formats={['avif', 'webp']}
                    />
                    <RenderSource
                        imgSrc={imgDeviceSet.desktop.src}
                        mediaMaxWidth={deviceSize.desktop}
                    />
                </>
            )}
        </>
    );
};

export const cssBackgroundSet = ({
    img: {
        src,
        optimizeOptions = {
            quality: 80,
        },
    },
    formats = ['avif', 'webp', 'jpeg'],
}: Omit<RenderOptimizedVariantsPropsType, 'formats'> & {
    formats?: RenderOptimizedVariantsPropsType['formats'];
}): string => {
    const common = useCommon();
    const appLayoutsState = AppLayoutsState.get(common);

    const output: Array<string> = [];

    for (const format of formats) {
        const optimizedUrl = appLayoutsState.optimizeImage(src, format, optimizeOptions);

        if (optimizedUrl !== null) {
            output.push(`url('${optimizedUrl}') type('image/${format}')`);
        }
    }

    if (output.length === 0) {
        return `background-image: url('${src}')`;
    }

    return `
        background-image: image-set(
            ${output.join(',\n')}
        );
    `;
};
